/**
 * The Exports catalog (P0-14: reporting/export gap closure) — pure data +
 * pure helpers, unit-tested separately from the screen that renders them.
 *
 * `ExportCsvButton` already existed and worked; what did not exist was a place
 * that told an administrator every export the product offers, in one list,
 * with what each one contains. This is that list. Every kind here maps 1:1 to
 * a `reporting:export`-gated `GET /api/admin-views/export/*` route — the
 * catalog is descriptive, not a second source of truth for what the server
 * will actually return.
 */

export type ExportKind = 'workforce' | 'exceptions' | 'verification' | 'payroll';

export interface ExportDescriptor {
  key: ExportKind;
  title: string;
  /** What the file contains, in plain language — shown on the card. */
  description: string;
  /** Whether this export accepts a since/until date range. */
  dateRange: boolean;
  fallbackFilename: string;
}

export const EXPORT_CATALOG: readonly ExportDescriptor[] = [
  {
    key: 'workforce',
    title: 'Workforce board',
    description:
      'Everyone currently clocked in, unscheduled no-shows, and sessions still open past the ' +
      'expected clock-out. A snapshot of this moment — there is no date range to choose, because ' +
      'the file is always "right now".',
    dateRange: false,
    fallbackFilename: 'cwos-workforce.csv',
  },
  {
    key: 'exceptions',
    title: 'Exceptions',
    description:
      'Every reviewable timekeeping exception in the period you choose — missing clock-outs, ' +
      'breadcrumb gaps, location gaps, overtime flags, and no-shows — with the worker, kind, ' +
      'severity, and status of each.',
    dateRange: true,
    fallbackFilename: 'cwos-exceptions.csv',
  },
  {
    key: 'verification',
    title: 'Check-in verification',
    description:
      'How check-ins verified over the period you choose, tallied by state (inside a geofence, ' +
      'outside, unavailable, worker-attested). Counts only — no coordinates.',
    dateRange: true,
    fallbackFilename: 'cwos-verification.csv',
  },
  {
    key: 'payroll',
    title: 'Approved hours (payroll)',
    description:
      'Approved hours per worker per pay period, frozen exactly as they stood at the moment each ' +
      'timesheet was approved. Hours only — no pay rate or dollar amount is calculated or exported.',
    dateRange: true,
    fallbackFilename: 'cwos-payroll.csv',
  },
] as const;

/** A `YYYY-MM-DD` value, as produced by an `<input type="date">`. */
export type DateOnly = string;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a well-formed `YYYY-MM-DD` string; false for '' or malformed input. */
export function isDateOnly(value: string): value is DateOnly {
  return DATE_ONLY_RE.test(value);
}

/**
 * A chosen since/until pair is invalid only if BOTH are given and until comes
 * before since. Either side may be blank (an open-ended range); blank is
 * never itself an error.
 */
export function isValidRange(since: string, until: string): boolean {
  if (since && !isDateOnly(since)) return false;
  if (until && !isDateOnly(until)) return false;
  if (since && until) return until >= since;
  return true;
}

/**
 * The exceptions/verification routes filter on a `datetime` (not date-only)
 * query param. Converts a date-only "since" into the start of that day UTC,
 * and a date-only "until" into the end of that day UTC, so a picked calendar
 * day is fully included on both ends.
 */
export function sinceIso(dateOnly: DateOnly): string {
  return `${dateOnly}T00:00:00.000Z`;
}
export function untilIso(dateOnly: DateOnly): string {
  return `${dateOnly}T23:59:59.999Z`;
}

/**
 * Counts DATA rows (excludes the header) in a downloaded CSV document, so the
 * screen can say "0 rows — nothing matched" rather than silently handing over
 * a header-only file with no explanation. Tolerant of the leading UTF-8 BOM
 * and CRLF line endings that `buildCsv` always produces, and of a trailing
 * blank line from the final terminator.
 */
export function countDataRows(csvText: string): number {
  const withoutBom = csvText.charCodeAt(0) === 0xfeff ? csvText.slice(1) : csvText;
  const lines = withoutBom.split('\r\n').filter((line) => line.length > 0);
  return Math.max(0, lines.length - 1);
}
